import { ApolloLink, Observable, Operation } from 'apollo-link';
import JwtDecode from 'jwt-decode';
import {
  EnvironmentVariableKeys,
  getEnvironmentVariable
} from '../environments/environment-variables';
import {
  IAuthTokenRequest,
  isFakeClientSdk,
  teamsSdk
} from '../teams-sdk/teamsSdk';
import {
  RING_ID,
  SESSION_ID
} from '../telemetry/session-context/SessionContext';
import { getFirstPartyResource } from './getFirstPartyResource';
import { IAuthTokenParsed } from './IAuthTokenParsed';
import { ITokenCacheObject } from './ITokenCacheObject';

export const CEHResource =
  process.env.REACT_APP_API_SERVER_OVERRIDE_3RDPARY === 'true'
    ? 'https://dev.ceh.teams.microsoft.com'
    : getFirstPartyResource();
export const MSGraphResource = 'https://graph.microsoft.com';
const tokenResources = [CEHResource, MSGraphResource];

type TokenMap = { [resource: string]: string };
const CACHE_PRE_EXPIRY_WINDOW = 120000;
let _tokenCache: { [resource: string]: ITokenCacheObject } = {};
let _correlationId = 0;

export const setTokenCache = (newTokenCache: {
  [resource: string]: ITokenCacheObject;
}) => {
  _tokenCache = newTokenCache;
};

/**
 * Max number of retries
 */
const RETRY_MAX = 2;

/**
 * the constant base of the retry delay, retry delay is an exponential multiple of this
 */
const RETRY_DELAY_BASE = 750;
export function getAccessTokensFromSDK(
  resources: string[] = tokenResources
): Promise<TokenMap> {
  return new Promise<TokenMap>((resolve, reject) => {
    // For Teams mode, we request the user's auth token from the Teams app via the Teams SDK
    const authTokenRequest: IAuthTokenRequest = {
      successCallback: (tokensString: string) => {
        let tokens = tokensString.split(',');
        let tokenMap: TokenMap = {};
        tokens.forEach((token, index) => {
          tokenMap[resources[index]] = token;
        });

        resolve(tokenMap);
      },
      failureCallback: (error: string) => {
        console.error(
          '[teamsAuth.getAccessTokenFromSDK] Error received when getting token from Teams Client SDK;' +
            (error ? JSON.stringify(error) : '')
        );
        reject(new Error(error));
      },
      resources: resources
    };
    teamsSdk.authentication.getAuthToken(authTokenRequest);
  });
}

export async function getAccessToken(
  resource: string,
  getTokenFromSDK: (resource: string) => Promise<string>,
  isExpired: (expiry: Date | null) => boolean
): Promise<string | null> {
  if (_tokenCache[resource]) {
    if (
      _tokenCache[resource].token &&
      _tokenCache[resource].expiry &&
      isExpired(_tokenCache[resource].expiry)
    ) {
      // valid unexpired token exists, use it
      return _tokenCache[resource].token;
    } else if (_tokenCache[resource].tokenPromise) {
      // existing request going on, return it
      return _tokenCache[resource].tokenPromise;
    }
    console.log(
      `[teamsAuth.getAccessToken] ${resource} request new token from SDK, existing token expired or broken >>`
    );
  } else {
    console.log(
      `[teamsAuth.getAccessToken] ${resource} request token from SDK >>`
    );
    // initiate the token cache object
    _tokenCache[resource] = {
      token: null,
      expiry: null,
      tokenPromise: null,
      retryCount: 0
    };
  }

  // either new token request without existing on-going request or token has expired
  // track reference to running sdk token request
  // (if another part of code requests token during this, we will return this promise using condition above)
  _tokenCache[resource].tokenPromise = _getTokenAndVerify(
    resource,
    getTokenFromSDK,
    isExpired
  );
  return _tokenCache[resource].tokenPromise;
}

async function _getTokenAndVerify(
  resource: string,
  getTokenFromSDK: (resource: string) => Promise<string>,
  isExpired: (expiry: Date | null) => boolean
): Promise<string> {
  let logPrefix = '[teamsAuth._getTokenAndVerify]';
  let retryAttempt: number = 0;
  if (_tokenCache[resource]?.retryCount) {
    retryAttempt = _tokenCache[resource].retryCount;
    logPrefix = `${logPrefix}[retryAttempt${retryAttempt}]`;
  }
  try {
    let token: string;
    // TODO (kbastien): Should we use moment here rather than Date?
    let requestDateTime = new Date().toISOString();
    try {
      token = await getTokenFromSDK(resource);
      const shouldVerify: boolean = !isFakeClientSdk();
      if (!shouldVerify) {
        return token;
      }
    } catch (err) {
      console.error(`${logPrefix} ${resource} Error getting token from sdk`, {
        name: err.name,
        message: err.message,
        ...err
      });
      throw err;
    }
    if (!token) {
      console.error(`${logPrefix} ${resource}. No token received from sdk!`);
      throw new Error('No token received from sdk!');
    }
    let tokenInfo: IAuthTokenParsed;
    try {
      tokenInfo = JwtDecode<IAuthTokenParsed>(token, { header: false });
    } catch (err) {
      console.error(`${logPrefix} ${resource}. Error parsing token`, {
        name: err.name,
        message: err.message,
        ...err
      });
      throw new Error(
        `${logPrefix} Error parsing token. ${err &&
          err.name + '::' + err.message}`
      );
    }

    if (
      tokenInfo.aud &&
      tokenInfo.exp &&
      tokenInfo.aud.toLowerCase() === resource.toLowerCase() &&
      tokenInfo.exp * 1000 > Date.now()
    ) {
      let expiry = new Date(tokenInfo.exp * 1000);
      console.log(`${logPrefix} received token`, {
        resource,
        requestDateTime,
        expiry,
        retryAttempt,
        token
      });
      _tokenCache[resource].token = token;
      _tokenCache[resource].expiry = expiry;
      // remove ref to running sdk token request promise
      _tokenCache[resource].tokenPromise = null;
      // reset retry count
      _tokenCache[resource].retryCount = 0;
    } else {
      type Reason =
        | 'aud null'
        | 'exp null'
        | 'aud/resource mismatch'
        | 'expired'
        | 'none';
      let reason: Reason =
        (!tokenInfo.aud && 'aud null') ||
        (!tokenInfo.exp && 'exp null') ||
        (tokenInfo.aud.toLowerCase() !== resource.toLowerCase() &&
          'aud/resource mismatch') ||
        (tokenInfo.exp * 1000 <= Date.now() && 'expired') ||
        'none';
      let additionalData: { [k: string]: string } = {};
      if (reason === 'aud/resource mismatch') {
        additionalData = {
          resource: resource.toLowerCase(),
          audience: tokenInfo.aud.toLowerCase()
        };
      } else if (reason === 'expired') {
        additionalData = {
          exp: tokenInfo.exp.toString(),
          expiryMs: (tokenInfo.exp * 1000).toString(),
          expiry: new Date(tokenInfo.exp * 1000).toISOString(),
          nowMs: Date.now().toString(),
          now: new Date().toISOString()
        };
      }

      console.error(
        `${logPrefix} ${resource} Invalid token received from sdk.`,
        { resource, reason, requestDateTime, retryAttempt, ...additionalData }
      );
      throw new Error(
        `${logPrefix} ${resource} Invalid token received from sdk. reason:${reason}`
      );
    }
    return token;
  } catch (err) {
    if (_tokenCache[resource].retryCount >= RETRY_MAX) {
      console.error(
        `${logPrefix} ${resource} Reached max retry, failing overall operation.`
      );
      _tokenCache[resource].token = null;
      _tokenCache[resource].expiry = null;
      // remove ref to running sdk token request promise
      _tokenCache[resource].tokenPromise = null;
      _tokenCache[resource].retryCount = 0;
      throw err;
    } else {
      _tokenCache[resource].retryCount++;
      console.warn(
        `${logPrefix} ${resource} failed, retrying. Queue retry attempt=${_tokenCache[resource].retryCount}`
      );
      return new Promise<string>((resolve, _reject) => {
        setTimeout(() => {
          console.log(
            `${logPrefix} ${resource} Retry attempt #${_tokenCache[resource].retryCount}`
          );
          resolve(_getTokenAndVerify(resource, getTokenFromSDK, isExpired));
        }, RETRY_DELAY_BASE * Math.pow(2, _tokenCache[resource].retryCount));
      });
    }
  }
}

function _checkExpiry(expiry: Date | null): boolean {
  // if token is about to expire within a 2 minutes, start pestering sdk for new token in every request
  return (
    (expiry &&
      expiry instanceof Date &&
      expiry.valueOf() > Date.now() + CACHE_PRE_EXPIRY_WINDOW) ||
    false
  );
}

export async function getCachedAccessTokenFromSDK(
  resource: string
): Promise<string> {
  const tokenMap = await getAccessTokensFromSDK([resource]);
  return tokenMap[resource];
}

export async function getToken(
  resource: string = CEHResource
): Promise<string | null> {
  return await getAccessToken(
    resource,
    getCachedAccessTokenFromSDK,
    _checkExpiry
  );
}

export async function decorateApolloRequest(
  operation: Operation
): Promise<Operation> {
  const token = await getToken();
  const teamsAppId = getEnvironmentVariable(EnvironmentVariableKeys.teamsAppId);
  // tslint:disable-next-line: no-any ~ Generic function across all GraphQL Operations
  operation.setContext((prevContext: Record<string, any>) => {
    return {
      headers: {
        Authorization: 'Bearer ' + token,
        'x-ms-sessionid': SESSION_ID,
        'x-ms-correlationid': `${SESSION_ID}.${++_correlationId}`,
        'x-ms-ringid': RING_ID !== null ? RING_ID : undefined,
        'x-ms-teamsAppId': teamsAppId
      },
      ...prevContext
    };
  });
  return operation;
}

export function createRequestDecoratorLink(
  requestDecorator: (operation: Operation) => Promise<Operation>
): ApolloLink {
  return new ApolloLink(
    (operation, forward) =>
      new Observable(observer => {
        // tslint:disable-next-line: no-any ~ Disabling because the handler here should be able to act across all operations
        let handle: any;
        Promise.resolve(operation)
          .then(oper => requestDecorator(oper))
          .then(() => {
            if (forward) {
              handle = forward(operation).subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer)
              });
            }
          })
          .catch(observer.error.bind(observer));

        return () => {
          if (handle) {
            handle.unsubscribe();
          }
        };
      })
  );
}
